/**
 * UGC Polaroid Flip-Through
 *
 * 4 photos stacked with rotations. Top card "flings" away on next,
 * remaining cards shift up. Dismissed card reappears at back of stack.
 */

const TRANSITION_MS = 500;
const SLOT_CLASSES = [ 'block-ugc__photo--1', 'block-ugc__photo--2', 'block-ugc__photo--3', 'block-ugc__photo--4' ];
const DISMISSED = 'block-ugc__photo--dismissed';
const NO_TRANSITION = 'block-ugc__photo--no-transition';

export const UgcSlider = {
	init() {
		document.querySelectorAll( '.block-ugc' ).forEach( ( section ) => {
			const photos = Array.from( section.querySelectorAll( '.block-ugc__photo' ) )
				.sort( ( a, b ) => parseInt( a.dataset.photo ) - parseInt( b.dataset.photo ) );
			const captionWrap = section.querySelector( '.block-ugc__caption' );
			const captions = Array.from( section.querySelectorAll( '[data-caption]' ) );
			const counterEl = section.querySelector( '[data-ugc-current]' );
			const progressEl = section.querySelector( '[data-ugc-progress]' );
			const prevBtn = section.querySelector( '[data-ugc-prev]' );
			const nextBtn = section.querySelector( '[data-ugc-next]' );
			const total = photos.length;

			if ( total < 2 ) return;

			// Deepest slot this stack actually uses. Only slots 1..4 are styled, so
			// a stack of 2 or 3 photos bottoms out below 4 — hardcoding 4 would
			// apply a slot the layout never positions.
			const BACK_SLOT = Math.min( total, SLOT_CLASSES.length );

			let current = 0;
			let animating = false;
			let direction = 'next'; // 'next' or 'prev'

			// Bumped on every goNext()/goPrev() call. Deferred steps (setTimeout,
			// rAF) re-check it before touching the DOM — a stale callback from an
			// older interaction (autoplay tick racing a manual click, or two
			// manual clicks close together) bails instead of applying, so it can
			// never land a slot class after a newer call already moved on.
			let epoch = 0;

			// Set first caption active
			if ( captions.length ) {
				captions[ 0 ].classList.add( 'is-active' );
			}

			function clearSlots( el ) {
				SLOT_CLASSES.forEach( c => el.classList.remove( c ) );
				el.classList.remove( DISMISSED );
			}

			function getSlot( photoIdx ) {
				return ( ( photoIdx - current + total ) % total ) + 1;
			}

			function applySlots() {
				photos.forEach( ( p, i ) => {
					clearSlots( p );
					p.classList.add( 'block-ugc__photo--' + getSlot( i ) );
				} );
			}

			function updateUI() {
				if ( counterEl ) {
					counterEl.textContent = String( current + 1 ).padStart( 2, '0' );
				}
				if ( progressEl ) {
					progressEl.style.width = ( ( current + 1 ) / total ) * 100 + '%';
				}

				// Simple fade: toggle is-active on captions
				captions.forEach( ( c, i ) => {
					c.classList.toggle( 'is-active', i === current );
				} );
			}

			function goNext() {
				if ( animating ) return;
				animating = true;
				direction = 'next';
				const myEpoch = ++epoch;

				const dismissed = photos[ current ];
				clearSlots( dismissed );
				dismissed.classList.add( DISMISSED );

				const prev = current;
				current = ( current + 1 ) % total;

				photos.forEach( ( p, i ) => {
					if ( i === prev ) return;
					clearSlots( p );
					p.classList.add( 'block-ugc__photo--' + getSlot( i ) );
				} );

				updateUI();

				setTimeout( () => {
					if ( myEpoch !== epoch ) return; // superseded — newer call owns state now
					dismissed.classList.add( NO_TRANSITION );
					clearSlots( dismissed );
					dismissed.classList.add( SLOT_CLASSES[ BACK_SLOT - 1 ] );
					dismissed.style.opacity = '0';

					void dismissed.offsetHeight; // force reflow

					dismissed.classList.remove( NO_TRANSITION );
					requestAnimationFrame( () => {
						if ( myEpoch === epoch ) {
							dismissed.style.opacity = '';
						}
						animating = false;
					} );
				}, TRANSITION_MS + 50 );
			}

			function goPrev() {
				if ( animating ) return;
				animating = true;
				direction = 'prev';
				const myEpoch = ++epoch;

				const incomingIdx = ( current - 1 + total ) % total;
				const incoming = photos[ incomingIdx ];

				incoming.classList.add( NO_TRANSITION );
				clearSlots( incoming );
				incoming.classList.add( DISMISSED );

				void incoming.offsetHeight;

				incoming.classList.remove( NO_TRANSITION );

				requestAnimationFrame( () => {
					// Superseded — a newer click already moved things on and owns
					// `animating` now; don't touch it here.
					if ( myEpoch !== epoch ) return;
					current = incomingIdx;
					photos.forEach( ( p, i ) => {
						clearSlots( p );
						p.classList.add( 'block-ugc__photo--' + getSlot( i ) );
					} );
					updateUI();
					setTimeout( () => {
						if ( myEpoch === epoch ) animating = false;
					}, TRANSITION_MS );
				} );
			}

			// Events
			if ( nextBtn ) nextBtn.addEventListener( 'click', goNext );
			if ( prevBtn ) prevBtn.addEventListener( 'click', goPrev );

			// Touch swipe support (responsive — swipe on photos or content area)
			const swipeTarget = section.querySelector( '.block-ugc__content' ) || section;
			let touchStartX = 0;
			let touchStartY = 0;
			let touchEndX = 0;
			const SWIPE_THRESHOLD = 50;

			swipeTarget.addEventListener( 'touchstart', ( e ) => {
				touchStartX = e.changedTouches[ 0 ].screenX;
				touchStartY = e.changedTouches[ 0 ].screenY;
			}, { passive: true } );

			swipeTarget.addEventListener( 'touchend', ( e ) => {
				touchEndX = e.changedTouches[ 0 ].screenX;
				const touchEndY = e.changedTouches[ 0 ].screenY;
				const diffX = touchStartX - touchEndX;
				const diffY = Math.abs( touchStartY - touchEndY );

				// Only trigger if horizontal swipe is dominant and exceeds threshold
				if ( Math.abs( diffX ) > SWIPE_THRESHOLD && Math.abs( diffX ) > diffY ) {
					if ( diffX > 0 ) {
						goNext(); // swipe left = next
					} else {
						goPrev(); // swipe right = prev
					}
					// Reset autoplay on swipe
					clearInterval( autoplay );
					autoplay = setInterval( goNext, 5000 );
				}
			}, { passive: true } );

			// Autoplay
			let autoplay = setInterval( goNext, 5000 );
			section.addEventListener( 'mouseenter', () => clearInterval( autoplay ) );
			section.addEventListener( 'mouseleave', () => { autoplay = setInterval( goNext, 5000 ); } );

			// Backgrounding the tab can freeze an in-flight opacity transition
			// (Chrome throttles the animation timeline while hidden, so the
			// Animation's playState stays "running" but currentTime never
			// advances past 0) — the incoming photo is left permanently
			// invisible once the tab is foregrounded again. Autoplay makes
			// this MORE likely here: a transition is far more often in-flight
			// at the exact moment of backgrounding than with manual-only nav.
			// Pause autoplay while hidden, then cancel any stuck animations
			// and reassert the correct slot classes on resume.
			document.addEventListener( 'visibilitychange', () => {
				if ( document.hidden ) {
					clearInterval( autoplay );
					return;
				}
				photos.forEach( ( p ) => {
					if ( p.getAnimations ) {
						p.getAnimations().forEach( ( a ) => a.cancel() );
					}
				} );
				applySlots();
				autoplay = setInterval( goNext, 5000 );
			} );

			// Fix caption height to tallest text (prevent layout jumps)
			function setCaptionHeight() {
				if ( ! captionWrap ) return;
				captionWrap.style.height = 'auto';
				const wrapW = captionWrap.offsetWidth;
				let maxH = 0;
				captions.forEach( c => {
					const prev = c.style.cssText;
					c.style.cssText = `position:absolute;visibility:hidden;opacity:1;display:block;height:auto;width:${ wrapW }px;left:0;top:0;`;
					maxH = Math.max( maxH, c.getBoundingClientRect().height );
					c.style.cssText = prev;
				} );
				if ( maxH > 0 ) captionWrap.style.height = Math.ceil( maxH ) + 'px';
			}

			// Init
			setCaptionHeight();
			applySlots();
			updateUI();

			window.addEventListener( 'resize', setCaptionHeight );
		} );
	},
};
